import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';

interface Props {
  title?: string;
  hint?: string;
  onResult: (text: string) => void;
  onClose: () => void;
}

const REGION_ID = 'zifek-qr-scan-region';

/**
 * QrScannerModal — ouvre la caméra du téléphone pour scanner un code QR.
 * Fournit aussi une saisie manuelle en secours (si la caméra est refusée).
 */
export default function QrScannerModal({ title = 'Scanner un QR Zifek', hint, onResult, onClose }: Props) {
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const handledRef = useRef(false);
  const [cameraError, setCameraError] = useState('');
  const [manual, setManual] = useState('');
  const [starting, setStarting] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        const scanner = new Html5Qrcode(REGION_ID, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 } },
          (decodedText) => {
            if (handledRef.current) return;
            handledRef.current = true;
            onResult(decodedText);
          },
          () => {
            // erreurs de frame ignorées (bruit normal)
          }
        );
        if (!cancelled) setStarting(false);
      } catch {
        if (!cancelled) {
          setStarting(false);
          setCameraError(
            "Impossible d'accéder à la caméra. Autorisez l'accès ou saisissez le code manuellement."
          );
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      const scanner = scannerRef.current;
      if (scanner) {
        scanner
          .stop()
          .then(() => scanner.clear())
          .catch(() => {});
        scannerRef.current = null;
      }
    };
  }, [onResult]);

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-50" onClick={onClose} />
      <div className="fixed z-50 inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center sm:p-4">
        <div className="w-full sm:max-w-md bg-background-50 rounded-t-2xl sm:rounded-2xl border border-background-200/70 overflow-hidden">
          <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
            <h3 className="text-sm font-bold text-foreground-950 flex items-center gap-2">
              <i className="ri-scan-line text-primary-500"></i>
              {title}
            </h3>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-foreground-500"></i>
            </button>
          </div>

          <div className="p-4">
            <div className="relative rounded-xl overflow-hidden bg-black min-h-[280px] flex items-center justify-center">
              <div id={REGION_ID} className="w-full [&_video]:w-full [&_video]:h-auto" />
              {starting && !cameraError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/80">
                  <i className="ri-loader-4-line animate-spin text-2xl"></i>
                  <span className="text-xs">Ouverture de la caméra...</span>
                </div>
              )}
            </div>

            {hint && !cameraError && (
              <p className="mt-3 text-xs text-foreground-500 text-center">{hint}</p>
            )}

            {cameraError && (
              <p className="mt-3 text-xs text-accent-700 text-center leading-relaxed">{cameraError}</p>
            )}

            <div className="mt-4 pt-4 border-t border-background-200/70">
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">
                Saisie manuelle du code
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={manual}
                  onChange={(e) => setManual(e.target.value)}
                  placeholder="zifek://account/..."
                  className="flex-1 h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
                />
                <button
                  onClick={() => manual.trim() && onResult(manual.trim())}
                  className="px-4 h-11 rounded-full text-sm font-semibold bg-primary-500 text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  Valider
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}